import {
  Directive,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  Validator,
} from '@angular/forms';
import { QuillEditorBase } from 'ngx-quill';
import { Subject, Subscription } from 'rxjs';

@Directive()
export abstract class _MatQuillBase extends QuillEditorBase
  implements
    ControlValueAccessor,
    OnChanges,
    OnDestroy,
    Validator
{
  static ngAcceptInputType_disabled: boolean | string | null | undefined;
  static ngAcceptInputType_required: boolean | string | null | undefined;

  @Input() disabled = false;
  @HostBinding('class.floating') get shouldLabelFloat() {
    return this.focused || !this.empty;
  }
  @HostBinding('attr.aria-describedby') _describedBy = '';

  stateChanges = new Subject<void>();
  focused = false;
  abstract controlType: string;
  abstract id: string;

  private contentChangedSubscription: Subscription;
  private blurSubscription: Subscription;
  private focusSubscription: Subscription;
  private _pendingValue: any = null;

  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};

  constructor(
    @Optional() public ngControl: NgControl,
  ) {
    super();

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.contentChangedSubscription = this.onContentChanged?.subscribe(() => {
      this.stateChanges.next();
      this._onChange(this.value);
    });
    this.blurSubscription = this.onBlur?.subscribe(() => {
      this.focused = false;
      this._onTouched();
      this.stateChanges.next();
    });
    this.focusSubscription = this.onFocus?.subscribe(() => {
      this.focused = true;
      this.stateChanges.next();
    });
    this.onEditorCreated?.subscribe((editor: any) => {
      this.quillEditor = editor;
      if (this._pendingValue !== null && this._pendingValue !== undefined) {
        this.writeValue(this._pendingValue);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.stateChanges.next();
  }

  ngOnDestroy(): void {
    this.contentChangedSubscription?.unsubscribe();
    this.blurSubscription?.unsubscribe();
    this.focusSubscription?.unsubscribe();
    this.stateChanges.complete();
  }

  get empty() {
    return !this.value;
  }

  get value(): any {
    return this.quillEditor ? this.quillEditor.getContents() : '';
  }
  set value(val: any) {
    this.writeValue(val);
    this.stateChanges.next();
  }

  get errorState(): boolean {
    const control = this.ngControl?.control;
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  setDescribedByIds(ids: string[]): void {
    this._describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    if (!this.focused) {
      this.focus();
    }
  }

  writeValue(value: any): void {
    if (this.quillEditor) {
      if (typeof value === 'string') {
        this.quillEditor.clipboard.dangerouslyPasteHTML(value || '');
      } else {
        this.quillEditor.setContents(value || '');
      }
      this._pendingValue = null;
    } else {
      this._pendingValue = value;
    }
  }
  registerOnChange(fn: any): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.stateChanges.next();
  }

  blur(): void {
    if (this.editorElem && this.editorElem.childNodes?.length) {
      (this.editorElem.childNodes as NodeListOf<HTMLElement>)[0]?.blur?.();
    }
  }
  focus(): void {
    this.quillEditor?.focus();
  }
}
